import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Printer, CreditCard, CheckCircle, AlertCircle,
  Loader, ArrowLeft, Filter, RotateCcw, Download, X, Check
} from 'lucide-react';
import {
  getVentes, getVente, getConfigurations, getFacturePDF,
  createPaiement, getBonPaiementPDF, getVentePaiements,
} from '../services/api';
import { useBoutique } from '../context/BoutiqueContext';
import { useAuth } from '../context/AuthContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './Factures.css';

const fmt     = (n, devise = 'XOF') => new Intl.NumberFormat('fr-FR').format(n || 0) + ` ${devise}`;
const fdate   = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fdatetime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const STATUTS = {
  payee:           { label: 'Payée',     color:'#16a34a', bg:'#dcfce7' },
  dette_partielle: { label: 'Partielle', color:'#d97706', bg:'#fef3c7' },
  dette_complete:  { label: 'Impayée',   color:'#dc2626', bg:'#fee2e2' },
  annulee:         { label: 'Annulée',   color:'#64748b', bg:'#f1f5f9' },
};

const MODE_LABEL = {
  especes:'Espèces', wave:'Wave', orange_money:'Orange Money',
  carte_bancaire:'Carte bancaire', cheque:'Chèque', autre:'Autre',
};

export default function Factures() {
  const [ventes,         setVentes]         = useState([]);
  const [selectedVente,  setSelectedVente]  = useState(null);
  const [ventePaiements, setVentePaiements] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [submitting,     setSubmitting]     = useState(false);
  const [toast,          setToast]          = useState(null);
  const [lastPaiementId, setLastPaiementId] = useState(null);
  const [pdfLoading,     setPdfLoading]     = useState(false);

  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterSearch, setFilterSearch] = useState('');

  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementData, setPaiementData] = useState({ montant:'', modePaiement:'especes', reference:'', commentaire:'' });
  const [paiementTarget, setPaiementTarget] = useState(null);

  const [searchParams] = useSearchParams();
  const { user }  = useAuth();
  const { selectedBoutiqueId } = useBoutique();
  const isAdmin = ['admin-entreprise', 'super-admin'].includes(user?.role);

  const [config, setConfig] = useState({
    devise:'XOF', nom_entreprise:'Mon Entreprise', logo:'',
    ninea:'', rc:'', telephone:'', email:'', site_web:'', adresse:'',
    taux_change: { EUR: 655.957, USD: 600, GNF: 0.071, MAD: 65 },
  });
  // Devise d'affichage choisie par l'utilisateur (sur la page) — par défaut la devise de base
  const [displayDevise, setDisplayDevise] = useState('XOF');
  useEffect(() => { setDisplayDevise(config.devise || 'XOF'); }, [config.devise]);

  // Champs affichés sur la facture (configurables)
  const fc = { coordonnees: true, mentions: true, clientContact: true, vendeur: true, detailNom: true, detailCode: true, detailPrixUnitaire: true, detailQuantite: true, detailCodeBarres: false, ...(config.facture_champs || {}) };
  const piedPage = (config.facture_pied_page || '').trim();

  // Conversion : tous les montants sont stockés dans la devise de base (config.devise).
  // taux_change[X] = combien d'unités de devise de base vaut 1 unité de X.
  // → montant en X = montant_base / taux_change[X]
  const convert = (amount) => {
    if (!amount) return 0;
    if (displayDevise === (config.devise || 'XOF')) return amount;
    const rate = Number(config.taux_change?.[displayDevise]) || 0;
    if (rate <= 0) return amount; // taux manquant : ne pas convertir
    return amount / rate;
  };
  // Formatage avec conversion
  const f = (n) => {
    const v = convert(n);
    const decimals = displayDevise === 'XOF' || displayDevise === 'GNF' ? 0 : 2;
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v || 0) + ` ${displayDevise}`;
  };

  // Liste des devises disponibles (base + celles configurées)
  const devisesDispo = useMemo(() => {
    const base = config.devise || 'XOF';
    const others = Object.keys(config.taux_change || {}).filter(d => d !== base);
    return [base, ...others];
  }, [config.devise, config.taux_change]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), type === 'success' ? 8000 : 4000);
  };

  useEffect(() => {
    loadConfigurations();
    loadVentes();
    const id = searchParams.get('id');
    if (id) loadVente(id);
  }, [searchParams, selectedBoutiqueId]); // eslint-disable-line

  const loadConfigurations = async () => {
    try {
      const { data } = await getConfigurations();
      if (data.data) setConfig({ ...config, ...data.data });
    } catch { /* silencieux */ }
  };

  const loadVentes = async () => {
    setLoading(true);
    try {
      const { data } = await getVentes();
      setVentes(data.data || []);
    } catch { showToast('error', 'Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const loadVente = async (id) => {
    try {
      const { data } = await getVente(id);
      setSelectedVente(data.data);
      try {
        const p = await getVentePaiements(id);
        setVentePaiements(p.data.data || []);
      } catch { setVentePaiements([]); }
    } catch { showToast('error', 'Erreur de chargement de la facture'); }
  };

  const filtered = useMemo(() => {
    let r = ventes;
    if (selectedBoutiqueId && selectedBoutiqueId !== 'all')
      r = r.filter(v => v.boutiqueId === selectedBoutiqueId);
    if (filterStatut !== 'tous') r = r.filter(v => v.statut === filterStatut);
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      r = r.filter(v => v.numeroFacture?.toLowerCase().includes(s) ||
        v.clientId?.nom?.toLowerCase().includes(s) ||
        v.client?.nom?.toLowerCase().includes(s));
    }
    return r;
  }, [ventes, selectedBoutiqueId, filterStatut, filterSearch]);

  const nbPayees     = useMemo(() => ventes.filter(v => v.statut === 'payee').length,           [ventes]);
  const nbPartielles = useMemo(() => ventes.filter(v => v.statut === 'dette_partielle').length, [ventes]);
  const nbImpayees   = useMemo(() => ventes.filter(v => v.statut === 'dette_complete').length,  [ventes]);

  const imprimerFacture = async () => {
    if (!selectedVente) return;
    setPdfLoading(true);
    try {
      const res = await getFacturePDF(selectedVente._id);
      const url = URL.createObjectURL(new Blob([res.data], { type:'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch { showToast('error', 'Erreur lors de la génération du PDF'); }
    finally { setPdfLoading(false); }
  };

  const ouvrirBonPaiement = async (id) => {
    try {
      const res = await getBonPaiementPDF(id);
      const url = URL.createObjectURL(new Blob([res.data], { type:'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { showToast('error', 'Impossible de générer le bon de paiement'); }
  };

  const openPaiementModal = (vente) => {
    setPaiementTarget(vente);
    setPaiementData({ montant: vente.resteAPayer?.toString() || '', modePaiement:'especes', reference:'', commentaire:'' });
    setShowPaiementModal(true);
  };

  const handlePaiementSubmit = async (e) => {
    e.preventDefault();
    if (!paiementTarget) return;
    const montant = parseFloat(paiementData.montant);
    if (montant <= 0 || montant > paiementTarget.resteAPayer) {
      showToast('error', `Montant doit être entre 0 et ${f(paiementTarget.resteAPayer)}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await createPaiement({
        venteId: paiementTarget._id, montant,
        modePaiement: paiementData.modePaiement,
        reference: paiementData.reference,
        commentaire: paiementData.commentaire,
      });
      const pid = res.data?.data?._id;
      setLastPaiementId(pid);
      showToast('success', 'Paiement enregistré avec succès');
      setShowPaiementModal(false);
      loadVentes();
      if (selectedVente?._id) {
        const p = await getVentePaiements(selectedVente._id);
        setVentePaiements(p.data.data || []);
      }
      setSelectedVente(null);
      if (pid) ouvrirBonPaiement(pid);
    } catch (err) { showToast('error', err.response?.data?.message || "Erreur lors de l'enregistrement"); }
    finally { setSubmitting(false); }
  };

  const clientInfo = (v) => {
    if (v?.clientId && typeof v.clientId === 'object') return v.clientId;
    return v?.client || null;
  };

  if (loading) return (
    <div className="fa-loading"><Loader className="fa-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="fa-page">

      {/* Toast */}
      {toast && (
        <div className={`fa-toast fa-toast--${toast.type} no-print`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
          {toast.type === 'success' && lastPaiementId && (
            <button className="fa-toast-action" onClick={() => ouvrirBonPaiement(lastPaiementId)}>
              <Printer size={12}/> Réimprimer le bon
            </button>
          )}
        </div>
      )}

      {/* ── Header ── */}
      <div className="fa-header no-print">
        <div className="fa-header-left">
          {selectedVente && (
            <button className="fa-back-btn" onClick={() => { setSelectedVente(null); setVentePaiements([]); }}>
              <ArrowLeft size={16}/>
            </button>
          )}
          <div className="fa-header-icon"><FileText size={20}/></div>
          <div>
            <h1 className="fa-header-title">{selectedVente ? `Facture ${selectedVente.numeroFacture}` : 'Factures'}</h1>
            <p className="fa-header-sub">
              {selectedVente ? fdate(selectedVente.createdAt) : `${filtered.length} facture${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="fa-header-right">
          {!selectedVente && isAdmin && <BoutiqueSelector/>}
          {/* Sélecteur de devise — convertit tous les montants affichés à la volée */}
          {devisesDispo.length > 1 && (
            <div className="fa-devise-select" title="Convertir les montants dans une autre devise">
              <span className="fa-devise-label">💱 Devise :</span>
              <select value={displayDevise} onChange={e => setDisplayDevise(e.target.value)}>
                {devisesDispo.map(d => (
                  <option key={d} value={d}>
                    {d}{d === (config.devise || 'XOF') ? ' (base)' : ` · 1 = ${config.taux_change?.[d] || '?'} ${config.devise}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedVente && (
            <button className="fa-print-btn" onClick={imprimerFacture} disabled={pdfLoading}>
              {pdfLoading ? <><Loader size={14} className="fa-spin"/> PDF…</> : <><Download size={14}/> Télécharger PDF</>}
            </button>
          )}
        </div>
      </div>

      {/* ══════════════ LIST VIEW ══════════════ */}
      {!selectedVente && (
        <div className="fa-content no-print">

          {/* KPI */}
          <div className="fa-kpi-row">
            {[
              { label: 'Total',      value: ventes.length, color:'#6366f1', bg:'#eef2ff', icon: FileText    },
              { label: 'Payées',     value: nbPayees,      color:'#16a34a', bg:'#dcfce7', icon: CheckCircle },
              { label: 'Partielles', value: nbPartielles,  color:'#d97706', bg:'#fef3c7', icon: CreditCard  },
              { label: 'Impayées',   value: nbImpayees,    color:'#dc2626', bg:'#fee2e2', icon: AlertCircle },
            ].map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={i} className="fa-kpi" style={{ borderTopColor: k.color }}>
                  <div className="fa-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                  <div className="fa-kpi-val">{k.value}</div>
                  <div className="fa-kpi-label">{k.label}</div>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="fa-filters">
            <Filter size={13} style={{ color:'#94a3b8', flexShrink:0 }}/>
            <input className="fa-search" type="text" placeholder="N° facture, client…"
              value={filterSearch} onChange={e => setFilterSearch(e.target.value)}/>
            <select className="fa-select" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="tous">Tous les statuts</option>
              <option value="payee">Payée</option>
              <option value="dette_partielle">Partielle</option>
              <option value="dette_complete">Impayée</option>
              <option value="annulee">Annulée</option>
            </select>
            <button className="fa-reset-btn" onClick={() => { setFilterStatut('tous'); setFilterSearch(''); }}>
              <RotateCcw size={13}/> Réinitialiser
            </button>
            <span className="fa-count">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          {filtered.length === 0 ? (
            <div className="fa-empty">
              <div className="fa-empty-icon"><FileText size={32}/></div>
              <h3>Aucune facture trouvée</h3>
              <p>Modifiez les filtres pour voir plus de résultats</p>
            </div>
          ) : (
            <div className="fa-card">
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Statut</th>
                    <th>Montant</th>
                    <th>Reste à payer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const st  = STATUTS[v.statut] || STATUTS.annulee;
                    const cli = clientInfo(v);
                    return (
                      <tr key={v._id}>
                        <td><span className="fa-num">{v.numeroFacture}</span></td>
                        <td className="fa-date">{fdatetime(v.createdAt)}</td>
                        <td>
                          <div className="fa-client-cell">
                            <span className="fa-client-name">{cli?.nom || 'Anonyme'}</span>
                            {cli?.telephone && <span className="fa-client-tel">{cli.telephone}</span>}
                          </div>
                        </td>
                        <td>
                          <span className="fa-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                        </td>
                        <td className="fa-amount">{f(v.totalTTC)}</td>
                        <td>
                          {v.statut !== 'payee'
                            ? <span className="fa-reste">{f(v.resteAPayer || 0)}</span>
                            : <span className="fa-muted">—</span>}
                        </td>
                        <td>
                          <div className="fa-row-actions">
                            <button className="fa-btn fa-btn--view" title="Voir la facture" onClick={() => loadVente(v._id)}>
                              <FileText size={13}/>
                            </button>
                            {v.statut !== 'payee' && v.clientId && (
                              <button className="fa-btn fa-btn--pay" title="Enregistrer un paiement" onClick={() => openPaiementModal(v)}>
                                <CreditCard size={13}/>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════ INVOICE VIEW ══════════════ */}
      {selectedVente && (
        <div className="fa-invoice-wrap">
          <div className="facture-container">

            {/* En-tête */}
            <div className="facture-header">
              <div className="facture-header-left">
                {fc.coordonnees && config.logo && (
                  <img src={config.logo} alt="Logo" className="facture-logo"
                    onError={e => { e.target.style.display = 'none'; }}/>
                )}
                <div>
                  <h1 className="facture-entreprise">{config.nom_entreprise}</h1>
                  {fc.coordonnees && (
                    <div className="facture-entreprise-info">
                      {config.adresse   && <div>{config.adresse}</div>}
                      {config.telephone && <div>Tél : {config.telephone}</div>}
                      {config.email     && <div>Email : {config.email}</div>}
                    </div>
                  )}
                </div>
              </div>
              <div className="facture-header-right">
                <div className="facture-title-badge">FACTURE</div>
                <div className="facture-ref">
                  <div className="facture-ref-num">N° {selectedVente.numeroFacture}</div>
                  <div className="facture-ref-date">Date : {fdate(selectedVente.createdAt)}</div>
                  <div className="facture-ref-date">Heure : {new Date(selectedVente.createdAt).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })}</div>
                </div>
              </div>
            </div>

            {/* Infos légales + Client */}
            <div className="facture-parties">
              <div className="facture-party">
                <div className="facture-party-title">Fournisseur</div>
                <div className="facture-party-name">{config.nom_entreprise}</div>
                {fc.mentions && config.ninea    && <div className="facture-party-line"><strong>NINEA :</strong> {config.ninea}</div>}
                {fc.mentions && config.rc       && <div className="facture-party-line"><strong>RC :</strong> {config.rc}</div>}
                {fc.coordonnees && config.adresse  && <div className="facture-party-line">{config.adresse}</div>}
                {fc.coordonnees && config.telephone&& <div className="facture-party-line">Tél : {config.telephone}</div>}
                {fc.coordonnees && config.email    && <div className="facture-party-line">Email : {config.email}</div>}
              </div>
              <div className="facture-party">
                <div className="facture-party-title">Client</div>
                {(() => {
                  const c = clientInfo(selectedVente);
                  if (!c?.nom) return <div className="facture-party-anon">Client anonyme / passage</div>;
                  return (
                    <>
                      <div className="facture-party-name">{c.nom}</div>
                      {c.ninea     && <div className="facture-party-line"><strong>NINEA :</strong> {c.ninea}</div>}
                      {c.rccm      && <div className="facture-party-line"><strong>RCCM :</strong> {c.rccm}</div>}
                      {fc.clientContact && c.telephone && <div className="facture-party-line">Tél : {c.telephone}</div>}
                      {fc.clientContact && c.email     && <div className="facture-party-line">Email : {c.email}</div>}
                      {fc.clientContact && c.adresse   && <div className="facture-party-line">{c.adresse}</div>}
                      {c.type === 'entreprise' && c.representant?.nom && (
                        <div className="facture-representant">
                          <div className="facture-representant-title">Représentant</div>
                          <div>{c.representant.prenom} {c.representant.nom}
                            {c.representant.poste && <span className="facture-party-muted"> — {c.representant.poste}</span>}
                          </div>
                          {c.representant.telephone && <div>Tél : {c.representant.telephone}</div>}
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="facture-mode-paiement">
                  <strong>Mode de paiement :</strong> {MODE_LABEL[selectedVente.modePaiement] || selectedVente.modePaiement}
                </div>
              </div>
            </div>

            {/* Tableau produits */}
            <div className="facture-section-title">Détails de la vente</div>
            <table className="facture-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  {fc.detailQuantite && <th className="tc">Qté</th>}
                  {fc.detailPrixUnitaire && <th className="tr">Prix unitaire</th>}
                  <th className="tr">Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedVente.details.map((d, i) => {
                  const code = d.codeProduit || d.produit?.codeProduit;
                  const cb   = d.codeBarres || d.produit?.codeBarres;
                  const refs = [fc.detailCode && code, fc.detailCodeBarres && cb].filter(Boolean);
                  return (
                  <tr key={i} className={i % 2 === 0 ? '' : 'facture-row-alt'}>
                    <td>
                      {fc.detailNom && d.nomProduit}
                      {d.typeVente === 'gros' && <span className="facture-gros-badge">GROS</span>}
                      {refs.length > 0 && (
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{refs.join(' · ')}</div>
                      )}
                    </td>
                    {fc.detailQuantite && <td className="tc facture-qty">{d.quantite}</td>}
                    {fc.detailPrixUnitaire && <td className="tr facture-price">{f(d.prixUnitaire)}</td>}
                    <td className="tr facture-subtotal">{f(d.sousTotal)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Totaux */}
            <div className="facture-totaux-wrap">
              <div className="facture-totaux">
                {/* N'afficher HT séparément que si TVA effectivement appliquée */}
                {(selectedVente.montantTVA > 0 || selectedVente.remise > 0) && (
                  <div className="facture-total-row">
                    <span>Sous-total HT</span>
                    <span>{f(selectedVente.totalHT + (selectedVente.remise || 0))}</span>
                  </div>
                )}
                {selectedVente.remise > 0 && (
                  <div className="facture-total-row facture-total-row--remise">
                    <span>
                      {selectedVente.loyaltyCoupon?.code
                        ? `Bon fidélité${selectedVente.loyaltyCoupon.label ? ` (${selectedVente.loyaltyCoupon.label})` : ''}`
                        : 'Remise'}
                      {selectedVente.loyaltyCoupon?.code && (
                        <small style={{ display: 'block', color: '#64748b', fontWeight: 400 }}>
                          Code : {selectedVente.loyaltyCoupon.code}
                        </small>
                      )}
                    </span>
                    <span>− {f(selectedVente.remise)}</span>
                  </div>
                )}
                {selectedVente.montantTVA > 0 && (
                  <div className="facture-total-row">
                    <span>TVA ({selectedVente.tauxTVA}%)</span>
                    <span>{f(selectedVente.montantTVA)}</span>
                  </div>
                )}
                <div className="facture-total-ttc">
                  <span>Total à payer</span>
                  <span>{f(selectedVente.totalTTC)}</span>
                </div>
                <div className="facture-paiement-row">
                  <span>Montant payé</span>
                  <span className="facture-paid">{f(selectedVente.montantPaye)}</span>
                </div>
                {selectedVente.monnaieRendue > 0 && (
                  <div className="facture-monnaie">
                    <span>Monnaie à rendre</span>
                    <span>{f(selectedVente.monnaieRendue)}</span>
                  </div>
                )}
                {selectedVente.montantPaye < selectedVente.totalTTC && (
                  <div className="facture-reste">
                    <span>Reste à payer</span>
                    <span>{f(selectedVente.totalTTC - selectedVente.montantPaye)}</span>
                  </div>
                )}
                {selectedVente.montantPaye === selectedVente.totalTTC && selectedVente.monnaieRendue === 0 && (
                  <div className="facture-exact"><Check size={13}/> Montant exact payé</div>
                )}
              </div>
            </div>

            {/* Historique paiements partiels */}
            {(ventePaiements.length > 0 || ['dette_partielle','dette_complete'].includes(selectedVente.statut)) && (
              <div className="facture-paiements-section">
                <div className="facture-paiements-head">
                  <span>Paiements reçus ({ventePaiements.length})</span>
                  <span>Total encaissé : {f(ventePaiements.reduce((s, p) => s + p.montant, 0) || selectedVente.montantPaye)}</span>
                </div>
                {ventePaiements.length === 0 ? (
                  <p className="facture-paiements-empty">Aucun paiement partiel enregistré.</p>
                ) : (
                  <table className="facture-paiements-table">
                    <thead>
                      <tr>
                        <th>#</th><th>Date</th><th className="tr">Montant</th>
                        <th>Mode</th><th>Reçu par</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ventePaiements.map((p, i) => (
                        <tr key={p._id}>
                          <td className="facture-paiement-idx">#{i + 1}</td>
                          <td>
                            <div className="facture-paiement-date">{fdate(p.datePaiement)}</div>
                            <div className="facture-paiement-time">{new Date(p.datePaiement).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })}</div>
                          </td>
                          <td className="tr facture-paiement-amount">{f(p.montant)}</td>
                          <td>
                            <span className="facture-mode-badge">{p.modePaiement?.replace('_',' ')}</span>
                          </td>
                          <td className="facture-paiement-by">
                            {p.enregistrePar ? `${p.enregistrePar.prenom || ''} ${p.enregistrePar.nom || ''}`.trim() : '—'}
                          </td>
                          <td>
                            <button className="facture-bon-btn no-print" onClick={() => ouvrirBonPaiement(p._id)}>
                              <Printer size={11}/> Bon
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="2" className="facture-tfoot-label">Total encaissé</td>
                        <td className="tr facture-tfoot-total">{f(ventePaiements.reduce((s, p) => s + p.montant, 0))}</td>
                        <td colSpan="3" className="tr">
                          {selectedVente.resteAPayer > 0
                            ? <span className="facture-tfoot-reste">Reste dû : {f(selectedVente.resteAPayer)}</span>
                            : <span className="facture-tfoot-solde"><Check size={11}/> Soldé</span>}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}

            {/* Pied de page */}
            <div className="facture-footer">
              <div className="facture-signatures">
                <div className="facture-signature">
                  <div className="facture-signature-label">Signature vendeur</div>
                  {fc.vendeur && (
                    <div className="facture-signature-name">{selectedVente.vendeur?.prenom} {selectedVente.vendeur?.nom}</div>
                  )}
                  <div className="facture-signature-box"/>
                </div>
                <div className="facture-signature">
                  <div className="facture-signature-label">Signature client</div>
                  <div className="facture-signature-box"/>
                </div>
              </div>
              <div className="facture-footer-bottom">
                <p className="facture-merci">{piedPage || 'Merci pour votre confiance !'}</p>
                {fc.coordonnees && config.site_web && <p>{config.site_web}</p>}
                {fc.coordonnees && config.email    && <p>{config.email}</p>}
                <p className="facture-generated">Document généré le {fdatetime(new Date())}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ MODAL Paiement ══════════════ */}
      {showPaiementModal && paiementTarget && (
        <>
          <div className="fa-overlay no-print" onClick={() => setShowPaiementModal(false)}/>
          <div className="fa-modal no-print">
            <div className="fa-modal-head">
              <div>
                <h2 className="fa-modal-title"><CreditCard size={15}/> Enregistrer un paiement</h2>
                <p className="fa-modal-sub">{paiementTarget.numeroFacture}</p>
              </div>
              <button className="fa-modal-close" onClick={() => setShowPaiementModal(false)}><X size={16}/></button>
            </div>

            <form onSubmit={handlePaiementSubmit} className="fa-modal-body">
              <div className="fa-pay-summary">
                <div className="fa-pay-row">
                  <span>Total TTC</span><strong>{f(paiementTarget.totalTTC)}</strong>
                </div>
                <div className="fa-pay-row">
                  <span>Déjà payé</span>
                  <strong style={{color:'#16a34a'}}>{f(paiementTarget.montantPaye || 0)}</strong>
                </div>
                <div className="fa-pay-row fa-pay-row--owed">
                  <span>Reste à payer</span>
                  <strong>{f(paiementTarget.resteAPayer || 0)}</strong>
                </div>
              </div>

              <div className="fa-form-row2">
                <div className="fa-field">
                  <label>Montant (XOF) *</label>
                  <input type="number" min="1" max={paiementTarget.resteAPayer} required
                    value={paiementData.montant} autoFocus
                    onChange={e => setPaiementData({...paiementData, montant: e.target.value})}/>
                </div>
                <div className="fa-field">
                  <label>Mode de paiement</label>
                  <select value={paiementData.modePaiement}
                    onChange={e => setPaiementData({...paiementData, modePaiement: e.target.value})}>
                    <option value="especes">Espèces</option>
                    <option value="wave">Wave</option>
                    <option value="orange_money">Orange Money</option>
                    <option value="carte_bancaire">Carte bancaire</option>
                    <option value="cheque">Chèque</option>
                  </select>
                </div>
              </div>

              <div className="fa-field">
                <label>Référence <span className="fa-opt">(optionnel)</span></label>
                <input type="text" placeholder="N° transaction, chèque…"
                  value={paiementData.reference}
                  onChange={e => setPaiementData({...paiementData, reference: e.target.value})}/>
              </div>
              <div className="fa-field">
                <label>Commentaire <span className="fa-opt">(optionnel)</span></label>
                <textarea rows="2" placeholder="Note sur le paiement…"
                  value={paiementData.commentaire}
                  onChange={e => setPaiementData({...paiementData, commentaire: e.target.value})}/>
              </div>

              <div className="fa-modal-footer">
                <button type="button" className="fa-btn-cancel" onClick={() => setShowPaiementModal(false)}>Annuler</button>
                <button type="submit" className="fa-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="fa-spin"/> Enregistrement…</>
                    : <><Check size={13}/> Enregistrer le paiement</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
